import { useEffect, useRef, useState } from "react";
import { X, Send, ChevronDown, Phone } from "lucide-react";
import { PhoneInput } from "@/components/site/PhoneInput";
import { SITE_PHONE, SITE_PHONE_DISPLAY } from "@/routes-data";
import { normalizePhoneHref } from "@workspace/phone-format";
import { useLocale } from "@/i18n/LocaleContext";
import { CHAT_ES } from "./chat-es";
import { assetUrl } from "@/i18n/assets";
import type { Locale } from "@/i18n/locale";

/** Display translation; English source strings are the catalog keys. */
function trFor(locale: Locale) {
  return (s: string): string => {
    if (locale !== "es") return s;
    const v = CHAT_ES[s];
    if (v === undefined) {
      // Coverage is enforced by test/chat-catalog.test.ts; surface gaps in dev.
      if (import.meta.env?.DEV) console.error(`[i18n] Missing Spanish chat string: ${s}`);
      return s;
    }
    return v;
  };
}

/**
 * Axle Inc. lead-capture chat widget.
 *
 * A floating launcher (bottom-right) opens a one-question-at-a-time chat that
 * qualifies visitors for Parts / Axle orders / Service / Business-fleet needs.
 *
 * Behavior highlights:
 *  - Always asks for a phone number first.
 *  - Stores every answer immediately as local chat state.
 *  - Sends a PARTIAL lead email once a phone number exists and the visitor
 *    abandons (close/minimize, tab hidden, page unload, or inactivity).
 *  - Sends a COMPLETED lead email when a path finishes.
 *  - Dedups via a per-session id + sent flags persisted in sessionStorage, so
 *    we never send the same partial/completed twice.
 *
 * The chat flow is defined declaratively in STEPS below — each step has a bot
 * message plus either free-text input or a set of option buttons that route to
 * the next step.
 */

// Endpoint is root-relative; the shared proxy routes /api to the API server.
const LEAD_URL = "/api/axle-chat/lead";
// Send a partial after this much inactivity (ms) once a phone is captured.
const INACTIVITY_MS = 75_000;

const AGENT_IMG = assetUrl("/chat/agent.png");

type Role = "bot" | "user";
/** Catalog messages store the English source (bot template / option label)
 * and are rendered in the active language at display time, so switching
 * language mid-conversation re-renders the whole thread. */
type Msg = { role: Role; text: string; ts: number; catalog?: boolean; data?: Data };
type Data = Record<string, string>;

type Option = {
  label: string;
  value: string;
  next: string;
  /** Extra fields to merge when this option is chosen (used for path routing). */
  set?: Data;
};

type Step = {
  bot: string; // supports the {{first}} token
  kind: "phone" | "name" | "text" | "email" | "options" | "end";
  /** Where the answer is stored on the data object. */
  field?: string;
  placeholder?: string;
  /** Next step for input kinds (options carry their own next). */
  next?: string;
  options?: Option[];
  terminal?: boolean;
};

const TRAILER_TYPES = [
  "Utility trailer",
  "Boat trailer",
  "Equipment trailer",
  "Enclosed trailer",
  "RV/camper",
  "Livestock trailer",
  "Commercial trailer",
  "Other",
];

/** Helper: build a list of options that all route to the same next step. */
function opts(values: string[], next: string): Option[] {
  return values.map((v) => ({ label: v, value: v, next }));
}

const STEPS: Record<string, Step> = {
  // ---- Intro -----------------------------------------------------------
  start: {
    bot: "Hi, welcome to Axle Inc. I can help with parts, axle orders, service, or trailer repair questions.\n\nIn case we get disconnected, what's the best phone number to reach you?",
    kind: "phone",
    field: "phone",
    placeholder: "(574) 555-0123",
    next: "name",
  },
  name: {
    bot: "Thank you. What is your name?",
    kind: "name",
    field: "name",
    placeholder: "First and last name",
    next: "language",
  },
  // Preferred language is recorded on the lead only; the chat stays in English.
  language: {
    bot: "Thanks, {{first}}. What language would you prefer we use when we contact you?",
    kind: "options",
    field: "preferredLanguage",
    options: [
      ...opts(["English", "Spanish", "French", "Portuguese", "Chinese (Mandarin)", "Vietnamese", "Arabic"], "need"),
      { label: "Another language", value: "Another language", next: "language_other" },
    ],
  },
  language_other: {
    bot: "Which language do you prefer?",
    kind: "text",
    field: "preferredLanguage",
    placeholder: "Type the language",
    next: "need",
  },
  need: {
    bot: "What can we help you with today?",
    kind: "options",
    field: "primaryNeed",
    options: [
      { label: "I need parts", value: "Parts", next: "a_part" },
      { label: "I need to order an axle", value: "Axle order", next: "b_replace" },
      { label: "I need service or repair", value: "Service / Repair", next: "c_service" },
      { label: "I manage trailers for a business", value: "Business / Fleet", next: "d_business" },
      { label: "I'm not sure yet", value: "Not sure", next: "e_start" },
    ],
  },

  // ---- Path A: Parts ---------------------------------------------------
  a_part: {
    bot: "What type of part are you looking for?",
    kind: "options",
    field: "partType",
    options: opts(
      ["Axle parts", "Brake parts", "Suspension parts", "Hub, drum, or bearing parts", "Lights or electrical", "Trailer hardware", "Other"],
      "a_info",
    ),
  },
  a_info: {
    bot: "Do you have any trailer or axle information available?",
    kind: "options",
    field: "axleInfo",
    options: opts(
      ["Yes, I have an axle tag or part number", "Yes, I know the trailer make/model", "I have photos", "I'm not sure"],
      "a_details",
    ),
  },
  a_details: {
    bot: "Please share any part number, axle tag information, trailer make/model, or a short description of what you need.",
    kind: "text",
    field: "axleDetails",
    placeholder: "Type the details here…",
    next: "a_urgency",
  },
  a_urgency: {
    bot: "How soon do you need the part?",
    kind: "options",
    field: "urgency",
    options: opts(["Today", "This week", "Within 2 weeks", "Just checking availability or pricing"], "a_contact"),
  },
  a_contact: {
    bot: "What is the best way for our team to follow up?",
    kind: "options",
    field: "preferredContactMethod",
    options: [
      { label: "Call me", value: "Call", next: "a_done" },
      { label: "Text me", value: "Text", next: "a_done" },
      { label: "Email me", value: "Email", next: "a_email" },
    ],
  },
  a_email: {
    bot: "What is your email address?",
    kind: "email",
    field: "email",
    placeholder: "you@example.com",
    next: "a_done",
  },
  a_done: {
    bot: "Thanks, {{first}}. I'll send this to our parts team so they can check availability, pricing, and fitment. If you have photos of the axle tag or part, please have them ready when we contact you.",
    kind: "end",
    terminal: true,
  },

  // ---- Path B: Ordering an axle ---------------------------------------
  b_replace: {
    bot: "Are you replacing an existing axle or ordering one for a new build?",
    kind: "options",
    field: "axleOrderType",
    options: opts(["Replacing an existing axle", "Ordering for a new trailer/build", "Not sure"], "b_trailer"),
  },
  b_trailer: {
    bot: "What type of trailer is this for?",
    kind: "options",
    field: "trailerType",
    options: opts(TRAILER_TYPES, "b_measure"),
  },
  b_measure: {
    bot: "Do you have the axle tag, serial number, or measurements?",
    kind: "options",
    field: "axleInfo",
    options: opts(
      ["Yes, I have an axle tag or serial number", "Yes, I have measurements", "I can send photos", "No, I need help figuring it out"],
      "b_details",
    ),
  },
  b_details: {
    bot: "Please share any details you know, such as axle capacity, hub face measurement, spring center measurement, bolt pattern, brake size, or trailer make/model.",
    kind: "text",
    field: "axleDetails",
    placeholder: "Type the details here…",
    next: "b_count",
  },
  b_count: {
    bot: "How many axles do you need?",
    kind: "options",
    field: "axleCount",
    options: opts(["One axle", "Two axles", "Three or more", "Not sure"], "b_when"),
  },
  b_when: {
    bot: "When do you need the axle?",
    kind: "options",
    field: "urgency",
    options: opts(["As soon as possible", "This week", "Within 2 to 4 weeks", "Planning ahead or just pricing"], "b_done"),
  },
  b_done: {
    bot: "Thanks, {{first}}. Axle orders usually require a few fitment details, so our team will review this and contact you to confirm specs before pricing or ordering.",
    kind: "end",
    terminal: true,
  },

  // ---- Path C: Service or repair --------------------------------------
  c_service: {
    bot: "What kind of service or repair do you need?",
    kind: "options",
    field: "serviceType",
    options: opts(
      ["Axle repair or replacement", "Brake service", "Bearing service", "Suspension repair", "Trailer inspection", "Lighting or electrical repair", "General trailer repair", "Not sure"],
      "c_status",
    ),
  },
  c_status: {
    bot: "Is the trailer currently usable or is it down?",
    kind: "options",
    field: "trailerStatus",
    options: opts(["Usable, but needs service", "Not safe to use", "Broken down", "Not sure"], "c_trailer"),
  },
  c_trailer: {
    bot: "What type of trailer is it?",
    kind: "options",
    field: "trailerType",
    options: opts(TRAILER_TYPES, "c_desc"),
  },
  c_desc: {
    bot: "Please briefly describe what is happening or what you need repaired.",
    kind: "text",
    field: "description",
    placeholder: "Type the details here…",
    next: "c_when",
  },
  c_when: {
    bot: "When would you like to have it serviced?",
    kind: "options",
    field: "urgency",
    options: opts(["As soon as possible", "This week", "Next week", "Flexible"], "c_use"),
  },
  c_use: {
    bot: "Is this trailer used for personal or business use?",
    kind: "options",
    field: "businessType",
    options: [
      { label: "Personal", value: "Personal", next: "c_done" },
      { label: "Business", value: "Business", next: "c_done" },
    ],
  },
  c_done: {
    bot: "Thanks, {{first}}. I'll send this to our service team so they can review the issue and contact you about next steps.",
    kind: "end",
    terminal: true,
  },

  // ---- Path D: Business / fleet ---------------------------------------
  d_business: {
    bot: "What type of business do you operate?",
    kind: "options",
    field: "businessType",
    options: opts(
      ["Landscaping", "Construction", "Delivery/logistics", "Agriculture", "Equipment rental", "Municipal/government", "Trailer sales or repair", "Other"],
      "d_count",
    ),
  },
  d_count: {
    bot: "How many trailers do you operate or manage?",
    kind: "options",
    field: "numberOfTrailers",
    options: opts(["One trailer", "2 to 5 trailers", "6 to 10 trailers", "11 to 25 trailers", "More than 25 trailers"], "d_need"),
  },
  d_need: {
    bot: "What do you need help with?",
    kind: "options",
    field: "description",
    options: opts(["Parts ordering", "Service or repair", "Axle replacement", "Preventive maintenance", "Fleet support", "Pricing/account setup"], "d_timing"),
  },
  d_timing: {
    bot: "Is this an immediate need or are you looking to set up ongoing support?",
    kind: "options",
    field: "urgency",
    options: opts(["Immediate need", "Ongoing support", "Both"], "d_company"),
  },
  d_company: {
    bot: "What is the name of your business?",
    kind: "text",
    field: "companyName",
    placeholder: "Business name",
    next: "d_email",
  },
  d_email: {
    bot: "What is the best email address for follow-up?",
    kind: "email",
    field: "email",
    placeholder: "you@example.com",
    next: "d_done",
  },
  d_done: {
    bot: "Thanks, {{first}}. I'll send this to our team so they can review your business needs and follow up about parts, service, or fleet support.",
    kind: "end",
    terminal: true,
  },

  // ---- Path E: Not sure yet (routes into the right path) ---------------
  e_start: {
    bot: "No problem. Are you trying to fix something, replace something, or figure out what part you need?",
    kind: "options",
    field: "intent",
    options: [
      { label: "Fix something", value: "Fix something", next: "c_service", set: { primaryNeed: "Service / Repair" } },
      { label: "Replace a part", value: "Replace a part", next: "a_part", set: { primaryNeed: "Parts" } },
      { label: "Order an axle", value: "Order an axle", next: "b_replace", set: { primaryNeed: "Axle order" } },
      { label: "Get service", value: "Get service", next: "c_service", set: { primaryNeed: "Service / Repair" } },
      { label: "I need someone to help identify the issue", value: "Help identify the issue", next: "e_desc", set: { primaryNeed: "Help identify issue" } },
    ],
  },
  e_desc: {
    bot: "Please describe what you are working on or what problem you are trying to solve.",
    kind: "text",
    field: "description",
    placeholder: "Type the details here…",
    next: "e_photos",
  },
  e_photos: {
    bot: "Do you have photos of the trailer, axle tag, damaged part, or part you are trying to replace?",
    kind: "options",
    field: "hasPhotos",
    options: opts(["Yes", "No", "I can get photos later"], "e_done"),
  },
  e_done: {
    bot: "Thanks, {{first}}. I'll send this to our team so they can help identify the right next step.",
    kind: "end",
    terminal: true,
  },
};

function firstNameOf(data: Data, tr: (s: string) => string): string {
  const n = (data.name || "").trim();
  return n ? n.split(/\s+/)[0] : tr("there");
}

function renderBot(text: string, data: Data, tr: (s: string) => string): string {
  return tr(text).replace(/\{\{first\}\}/g, firstNameOf(data, tr));
}

function isValidPhone(v: string): boolean {
  return (v.match(/\d/g) || []).length >= 10;
}
function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function ChatWidget() {
  const { locale } = useLocale();
  const tr = trFor(locale);
  const trRef = useRef(tr);
  trRef.current = tr;
  const display = (m: Msg): string =>
    m.catalog
      ? m.role === "bot"
        ? renderBot(m.text, m.data ?? {}, trRef.current)
        : trRef.current(m.text)
      : m.text;
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [delivery, setDelivery] = useState<"pending" | "failed" | "sent" | null>(null);

  // Render state (mirrored into refs so event handlers always see fresh data).
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [stepId, setStepId] = useState<string>("start");
  const [data, setData] = useState<Data>({});

  const msgsRef = useRef<Msg[]>([]);
  const stepRef = useRef<string>("start");
  const dataRef = useRef<Data>({});

  const sessionIdRef = useRef<string>("");
  const createdAtRef = useRef<string>("");
  const partialSentRef = useRef(false);
  const completedSentRef = useRef(false);
  const inactivityRef = useRef<number | undefined>(undefined);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // --- small state helpers (keep refs + state in sync) ----------------
  function pushMsg(m: Msg) {
    const next = [...msgsRef.current, m];
    msgsRef.current = next;
    setMsgs(next);
  }
  function setStepBoth(id: string) {
    stepRef.current = id;
    setStepId(id);
  }
  function setDataBoth(next: Data) {
    dataRef.current = next;
    setData(next);
  }

  function setSent(status: "partial" | "completed", val: boolean) {
    if (status === "partial") partialSentRef.current = val;
    else completedSentRef.current = val;
  }

  // --- send the lead to the backend (dedup-guarded) -------------------
  async function sendLead(status: "partial" | "completed", preferBeacon = false): Promise<boolean> {
    const d = dataRef.current;
    if (!d.phone) return false; // never send without a phone number
    if (status === "partial" && (partialSentRef.current || completedSentRef.current)) return true;
    if (status === "completed" && completedSentRef.current) return true;

    // Optimistically mark as sent so simultaneous abandonment events don't
    // double-fire. Rolled back below if delivery fails, so the lead can still
    // be retried by a later trigger (and never silently lost).
    setSent(status, true);

    const now = new Date().toISOString();
    const payload = {
      sessionId: sessionIdRef.current,
      status,
      name: d.name || "",
      phone: d.phone || "",
      email: d.email || "",
      preferredLanguage:
        d.preferredLanguage && d.preferredLanguage !== "Another language"
          ? d.preferredLanguage
          : "English",
      companyName: d.companyName || "",
      primaryNeed: d.primaryNeed || "",
      trailerType: d.trailerType || "",
      partType: d.partType || "",
      axleDetails: d.axleDetails || "",
      serviceType: d.serviceType || "",
      businessType: d.businessType || "",
      numberOfTrailers: d.numberOfTrailers || "",
      urgency: d.urgency || "",
      description: d.description || "",
      preferredContactMethod: d.preferredContactMethod || "",
      transcript: msgsRef.current.map((m) => ({
        role: m.role,
        text: display(m),
        ts: new Date(m.ts).toISOString(),
      })),
      currentStep: stepRef.current,
      createdAt: createdAtRef.current,
      completedAt: status === "completed" ? now : "",
      abandonedAt: status === "partial" ? now : "",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };

    const bodyStr = JSON.stringify(payload);

    // Beacon path for unload-style events (most reliable when the page is
    // going away). If the browser refuses to queue it, fall through to fetch.
    if (preferBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([bodyStr], { type: "application/json" });
        if (navigator.sendBeacon(LEAD_URL, blob)) return true;
      } catch {
        /* fall through to fetch */
      }
    }

    // Fetch path (keepalive lets it survive a slow navigation). Roll back the
    // sent flag on failure so a later abandonment trigger can retry the lead.
    try {
      const response = await fetch(LEAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyStr,
        keepalive: true,
      });
      if (!response.ok) {
        setSent(status, false);
        return false;
      }
      return true;
    } catch {
      setSent(status, false);
      return false;
    }
  }

  function resetInactivity() {
    if (inactivityRef.current) window.clearTimeout(inactivityRef.current);
    inactivityRef.current = window.setTimeout(() => {
      if (dataRef.current.phone && !partialSentRef.current && !completedSentRef.current) {
        sendLead("partial");
      }
    }, INACTIVITY_MS);
  }

  // --- advance the conversation ---------------------------------------
  async function enterStep(id: string, baseData: Data) {
    const step = STEPS[id];
    setStepBoth(id);
    setError("");
    if (step.terminal) {
      setDelivery("pending");
      const ok = await sendLead("completed");
      setDelivery(ok ? "sent" : "failed");
      if (!ok) return;
    }
    pushMsg({ role: "bot", text: step.bot, data: baseData, catalog: true, ts: Date.now() });
  }

  function chooseOption(o: Option) {
    const step = STEPS[stepRef.current];
    pushMsg({ role: "user", text: o.label, catalog: true, ts: Date.now() });
    const next: Data = { ...dataRef.current };
    if (step.field) next[step.field] = o.value;
    if (o.set) Object.assign(next, o.set);
    setDataBoth(next);
    resetInactivity();
    enterStep(o.next, next);
  }

  function submitInput(e: React.FormEvent) {
    e.preventDefault();
    const step = STEPS[stepRef.current];
    const val = input.trim();

    if (step.kind === "phone" && !isValidPhone(val)) {
      setError(tr("Please enter a valid phone number."));
      return;
    }
    if (step.kind === "email" && !isValidEmail(val)) {
      setError(tr("Please enter a valid email address."));
      return;
    }
    if ((step.kind === "name" || step.kind === "text") && val.length === 0) {
      setError(tr("Please enter a response."));
      return;
    }
    if (step.field === "preferredLanguage" && val.length > 80) {
      setError(tr("Please keep the language to 80 characters or fewer."));
      return;
    }

    pushMsg({ role: "user", text: val, ts: Date.now() });
    const next: Data = { ...dataRef.current };
    if (step.field) next[step.field] = val;
    setDataBoth(next);
    setInput("");
    resetInactivity();
    if (step.next) enterStep(step.next, next);
  }

  // --- open / close ----------------------------------------------------
  function closeChat() {
    setOpen(false);
    // Closing/minimizing after a phone number => partial lead.
    if (dataRef.current.phone && !partialSentRef.current && !completedSentRef.current) {
      sendLead("partial");
    }
  }
  function openChat() {
    setOpen(true);
    resetInactivity();
  }

  // --- init (runs once on client) -------------------------------------
  useEffect(() => {
    // A fresh id per page load represents one "chat attempt". The partial and
    // completed emails within this attempt share it; a reload is treated as a
    // new attempt, so a new chat is never blocked by a prior attempt's flags.
    try {
      sessionIdRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
    } catch {
      sessionIdRef.current = Math.random().toString(36).slice(2);
    }
    createdAtRef.current = new Date().toISOString();

    // Seed the first bot message.
    msgsRef.current = [{ role: "bot", text: STEPS.start.bot, data: {}, catalog: true, ts: Date.now() }];
    setMsgs(msgsRef.current);
    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- abandonment listeners ------------------------------------------
  useEffect(() => {
    if (!mounted) return;
    const partialOnExit = () => {
      if (dataRef.current.phone && !partialSentRef.current && !completedSentRef.current) {
        sendLead("partial", true);
      }
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") partialOnExit();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", partialOnExit);
    window.addEventListener("beforeunload", partialOnExit);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", partialOnExit);
      window.removeEventListener("beforeunload", partialOnExit);
      if (inactivityRef.current) window.clearTimeout(inactivityRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs, open]);

  // Focus the text input when a free-text step becomes active.
  useEffect(() => {
    const step = STEPS[stepId];
    if (open && step && step.kind !== "options" && step.kind !== "end") {
      const t = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [stepId, open]);

  if (!mounted) return null;

  const step = STEPS[stepId];
  const inputType =
    step.kind === "phone" ? "tel" : step.kind === "email" ? "email" : "text";
  const inputMode = step.kind === "phone" ? "tel" : step.kind === "email" ? "email" : "text";

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={openChat}
          aria-label={tr("Open chat — chat with us")}
          className="group fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-50 flex items-center gap-3 rounded-full bg-secondary text-white pl-1.5 pr-5 py-1.5 shadow-2xl shadow-black/30 ring-1 ring-white/10 transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="relative inline-flex">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
            <img
              src={AGENT_IMG}
              alt=""
              aria-hidden="true"
              width={96}
              height={96}
              className="relative h-12 w-12 rounded-full object-cover ring-2 ring-white/80"
              loading="lazy"
              decoding="async"
            />
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-400 ring-2 ring-secondary" />
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">Axle Inc.</span>
            <span className="text-sm font-semibold">{tr("Chat with us")}</span>
          </span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          role="dialog"
          aria-label={tr("Axle Inc. chat")}
          className="fixed z-50 bottom-3 right-3 left-3 sm:left-auto sm:bottom-5 sm:right-5 flex flex-col w-auto sm:w-[384px] h-[78vh] sm:h-[560px] max-h-[calc(100dvh-1.5rem)] rounded-2xl border border-border bg-background shadow-2xl shadow-black/40 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="flex items-center gap-3 bg-secondary px-4 py-3 text-white">
            <span className="relative inline-flex shrink-0">
              <img
                src={AGENT_IMG}
                alt=""
                aria-hidden="true"
                width={80}
                height={80}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-white/70"
              />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-secondary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Axle Inc.</p>
              <p className="text-[11px] text-white/60 leading-tight">{tr("Trailer care experts • Typically replies in minutes")}</p>
            </div>
            <button
              type="button"
              onClick={closeChat}
              aria-label={tr("Minimize chat")}
              className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={closeChat}
              aria-label={tr("Close chat")}
              className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3 bg-muted/30">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-background text-foreground border border-border rounded-bl-sm"
                  }`}
                >
                  {display(m)}
                </div>
              </div>
            ))}
          </div>

          {/* Input / options */}
          <div className="border-t border-border bg-background p-3">
            {step.kind === "options" && step.options && (
              <div className="flex flex-col gap-2 max-h-[44vh] overflow-y-auto">
                {step.options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => chooseOption(o)}
                    className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-left text-sm font-medium text-foreground hover:border-primary hover:bg-primary/5 active:scale-[0.99] transition-all"
                  >
                    {tr(o.label)}
                  </button>
                ))}
              </div>
            )}

            {(step.kind === "phone" || step.kind === "name" || step.kind === "text" || step.kind === "email") && (
              <form onSubmit={submitInput} className="space-y-1.5">
                <div className="flex items-end gap-2">
                  {step.kind === "phone" ? (
                    <PhoneInput
                      ref={inputRef}
                      inputMode="tel"
                      autoComplete="tel"
                      value={input}
                      onValueChange={(nextValue) => {
                        setInput(nextValue);
                        if (error) setError("");
                      }}
                      placeholder={step.placeholder ? tr(step.placeholder) : undefined}
                      aria-label={tr(step.placeholder || "Your answer")}
                      className={`flex-1 rounded-xl border bg-background px-3.5 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                        error ? "border-primary" : "border-border"
                      }`}
                    />
                  ) : (
                    <input
                      ref={inputRef}
                      type={inputType}
                      inputMode={inputMode as "tel" | "email" | "text"}
                      autoComplete={step.kind === "email" ? "email" : step.kind === "name" ? "name" : "off"}
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder={step.placeholder ? tr(step.placeholder) : undefined}
                      aria-label={tr(step.placeholder || "Your answer")}
                      className={`flex-1 rounded-xl border bg-background px-3.5 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                        error ? "border-primary" : "border-border"
                      }`}
                    />
                  )}
                  <button
                    type="submit"
                    aria-label={tr("Send")}
                    className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                {error && <p className="text-xs text-primary-strong">{error}</p>}
              </form>
            )}

            {step.kind === "end" && (
              <div className="flex flex-col gap-2">
                {delivery === "sent" && <p className="text-center text-xs text-muted-foreground">
                  {tr("Our team has your details and will reach out shortly.")}
                </p>}
                {delivery === "pending" && <p role="status" className="text-center text-xs text-muted-foreground">{tr("Sending your details…")}</p>}
                {delivery === "failed" && <>
                  <p role="alert" className="text-center text-xs text-primary-strong">{tr("We couldn't submit your details. Please retry or call us.")}</p>
                  <button type="button" className="text-sm underline" onClick={() => void enterStep(stepRef.current, dataRef.current)}>{tr("Retry submission")}</button>
                </>}
                <a
                  href={`tel:${normalizePhoneHref(SITE_PHONE)}`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium px-4 py-2.5 hover:bg-primary/90 transition-colors"
                >
                  <Phone className="h-4 w-4" /> {tr("Call")} {SITE_PHONE_DISPLAY}
                </a>
              </div>
            )}

            <p className="mt-2 text-center text-[10px] text-muted-foreground">
              {tr("Powered by Axle Inc. • We'll only use this to help you.")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
